/*
  TGP 2026-10-20. Footer sign-up ("Join the Tribe") straight to a Klaviyo list.

  The section renders the theme's own email form, so nothing shifts or flashes in. With a Klaviyo
  list ID set in the section, this script sends the email to that list through Klaviyo's client
  Subscriptions API (public site key only) and shows the success message in place. Klaviyo's list
  settings decide single or double opt-in, and list-triggered flows fire as for any Klaviyo form.

  data-list may hold several list IDs, comma-separated: the first must accept the sign-up (else the Shopify
  fallback runs); the others are added best-effort.
  Optional on the wrapper: data-properties (JSON, set on the Klaviyo profile, e.g. {"bfcm_member_2026":true}
  on the members page) and data-event (published to Shopify customer events on success, for custom pixels).
  Optional data-meta-pixel: a Meta Pixel ID that gets a Lead event on success (trackSingle, so only that Pixel
  counts it, not other Pixels loaded on the page). Sent only if the page already loads Meta's fbq.
  Klaviyo's own forms (the pop-ups): when the script tag has data-meta-pixel, each Klaviyo form submit sends the
  same Lead event, once per form per page view. The script can be included more than once on a page (footer and
  members page); only the first copy runs.
  Optional SMS: data-sms-list (comma-separated list IDs) plus an input with data-signup-phone. A filled-in phone
  number is sent as E.164 with SMS marketing consent to those lists: a list that is also in data-list gets the
  email and SMS consent in one call, an SMS-only list gets SMS consent only. A blank phone sends email only. If
  Klaviyo refuses the phone number, the email sign-up is retried without it and [data-signup-sms-failed] shows.

  Fallback: without JavaScript, or if Klaviyo can't be reached or refuses the request, the form
  submits to Shopify exactly as before (customer with email consent, tag "newsletter"), so a
  sign-up is never lost.
*/
(() => {
  if (window.sisuKlaviyoSignupLoaded) return;
  window.sisuKlaviyoSignupLoaded = true;
  const REVISION = '2026-10-15';
  const scriptPixel = document.currentScript && document.currentScript.dataset.metaPixel;

  const sendLead = (pixel, source) => {
    try {
      if (!pixel || typeof window.fbq !== 'function') return;
      const eventID = window.crypto && crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
      fbq('trackSingle', pixel, 'Lead', { content_name: source || '' }, { eventID });
    } catch (error) {}
  };

  // US and Canada (+1) numbers as typed, or any number already in +country format. null = not a valid number.
  const toE164 = (raw) => {
    const digits = raw.replace(/\D/g, '');
    if (raw.trim().startsWith('+')) return digits.length >= 8 && digits.length <= 15 ? `+${digits}` : null;
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits[0] === '1') return `+${digits}`;
    return null;
  };

  document.querySelectorAll('[data-klaviyo-signup]').forEach((root) => {
    const form = root.querySelector('form');
    const input = form && form.querySelector('input[type="email"]');
    const status = root.querySelector('[data-signup-status]');
    if (!form || !input || !status || !root.dataset.list || !root.dataset.company) return;
    const button = form.querySelector('[type="submit"]');
    const phoneInput = root.dataset.smsList ? form.querySelector('[data-signup-phone]') : null;
    const phoneError = root.querySelector('[data-signup-phone-error]');
    const smsFailed = root.querySelector('[data-signup-sms-failed]');

    form.addEventListener('submit', async (event) => {
      if (!input.checkValidity()) return; // the browser shows its own message
      event.preventDefault();
      if (form.getAttribute('aria-busy') === 'true') return;

      let phone = null;
      if (phoneInput && phoneInput.value.trim()) {
        phone = toE164(phoneInput.value);
        if (!phone) {
          phoneInput.setAttribute('aria-invalid', 'true');
          if (phoneError) phoneError.hidden = false;
          phoneInput.focus();
          return;
        }
      }
      if (phoneInput) phoneInput.removeAttribute('aria-invalid');
      if (phoneError) phoneError.hidden = true;

      form.setAttribute('aria-busy', 'true');
      if (button) button.disabled = true;

      const email = input.value.trim();
      let properties = null;
      try {
        if (root.dataset.properties) properties = JSON.parse(root.dataset.properties);
      } catch (error) {}
      const ids = (value) => (value || '').split(',').map((id) => id.trim()).filter(Boolean);
      const lists = ids(root.dataset.list);
      const smsLists = phone ? ids(root.dataset.smsList) : [];
      const profileFor = (withEmail, withSms) => {
        const profile = { email, subscriptions: {} };
        if (withEmail) profile.subscriptions.email = { marketing: { consent: 'SUBSCRIBED' } };
        if (withSms) {
          profile.phone_number = phone;
          profile.subscriptions.sms = { marketing: { consent: 'SUBSCRIBED' } };
        }
        if (properties) profile.properties = properties;
        return profile;
      };
      const body = (list, profile) => ({
        data: {
          type: 'subscription',
          attributes: {
            custom_source: root.dataset.source || 'Footer sign-up',
            profile: {
              data: {
                type: 'profile',
                attributes: profile,
              },
            },
          },
          relationships: { list: { data: { type: 'list', id: list } } },
        },
      });
      const subscribe = (list, profile) =>
        fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(root.dataset.company)}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/vnd.api+json', revision: REVISION },
          body: JSON.stringify(body(list, profile)),
        });

      let smsRefused = false;
      try {
        const first = lists[0];
        let response = await subscribe(first, profileFor(true, smsLists.includes(first)));
        if (!response.ok && smsLists.includes(first)) {
          smsRefused = true; // most likely the phone number; keep the email sign-up
          response = await subscribe(first, profileFor(true, false));
        }
        if (!response.ok) throw new Error(`Klaviyo ${response.status}`);
        const rest = lists.slice(1).map((list) => subscribe(list, profileFor(true, !smsRefused && smsLists.includes(list))));
        const smsOnly = smsRefused ? [] : smsLists.filter((list) => !lists.includes(list)).map((list) => subscribe(list, profileFor(false, true)));
        await Promise.all([...rest, ...smsOnly].map((call) => call.catch(() => {})));
      } catch (error) {
        form.submit(); // Shopify fallback; native submit() skips this handler
        return;
      }

      // Tie this browser to the profile, so Klaviyo forms set to skip subscribers stay hidden.
      try {
        if (window.klaviyo && typeof window.klaviyo.identify === 'function') window.klaviyo.identify({ email });
      } catch (error) {}

      try {
        if (root.dataset.event && window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
          Shopify.analytics.publish(root.dataset.event, { source: root.dataset.source || '' });
        }
      } catch (error) {}

      sendLead(root.dataset.metaPixel, root.dataset.source);

      if (smsRefused && smsFailed) smsFailed.hidden = false;
      form.hidden = true;
      status.hidden = false;
      status.focus();
    });
  });

  if (scriptPixel) {
    const sent = new Set();
    window.addEventListener('klaviyoForms', (event) => {
      const detail = event.detail || {};
      if (detail.type !== 'submit' || sent.has(detail.formId)) return;
      sent.add(detail.formId);
      sendLead(scriptPixel, `Klaviyo form ${detail.formId}`);
    });
  }
})();
